import { categoryById, type Category, type Suggestion } from "./categories";

export interface LandingLink { to: string; label: string; blurb: string }
export interface LandingPage {
  slug: string;
  title: string;
  description: string;
  h1: string;
  subtitle: string;
  spinLabel: string;
  intro: string[];
  links: LandingLink[];
  faqs: { q: string; a: string }[];
  mode?: "names";
  subtitleH2?: boolean;
  ideasLabel?: string;
}

const s = (label: string, ...tags: string[]): Suggestion => ({ label, tags });

const DATE_IDEAS: Suggestion[] = [
  s("Cook a new recipe together", "stay-in", "dinner", "free"), s("Candlelit takeaway night", "stay-in", "dinner", "cosy"), s("Film night & blanket fort", "stay-in", "cosy", "free"),
  s("Try a restaurant neither of you has been to", "out", "dinner"), s("Sunset walk & ice cream", "out", "free"), s("Cocktail bar date", "out"),
  s("Board game showdown", "stay-in", "cosy", "free"), s("Picnic in the park", "out", "free", "dinner"), s("Late-night cinema", "out", "cosy"),
  s("Comedy club", "out"), s("Pottery or painting class", "out"), s("Pizza from scratch", "stay-in", "dinner", "cosy"),
  s("Stargazing with hot chocolate", "out", "free", "cosy"), s("Pub quiz as a team of two", "out"), s("Spa night at home", "stay-in", "cosy", "free"),
  s("Crazy golf rematch", "out"), s("Breakfast-for-dinner date", "stay-in", "dinner", "free"), s("Live music gig", "out"),
  s("Bookshop date & coffee", "out", "cosy"), s("Living-room karaoke duet", "stay-in", "free"),
];

const base = (id: string) => categoryById(id) as Category;
const mergeFilters = (cats: Category[]) => cats.flatMap((c) => c.filters).filter((f, i, a) => a.findIndex((x) => x.id === f.id) === i);

export const LANDING_CATEGORIES: Record<string, () => Category> = {
  couples: () => ({ ...base("night-out"), id: "date-night", name: "Date Night", question: "What's our date night?", tone: "pink",
    filters: [{ id: "stay-in", label: "Stay in" }, { id: "out", label: "Go out" }, { id: "dinner", label: "Dinner" }, { id: "cosy", label: "Cosy" }, { id: "free", label: "Free" }],
    items: DATE_IDEAS }),
  "what-should-i-eat": () => base("food"),
  "random-food-picker": () => base("food"),
  "couple-decision-maker": () => LANDING_CATEGORIES.couples(),
  "who-does-the-dishes": () => base("chores"),
  "what-should-we-watch": () => base("tv-film"),
  "what-should-we-do-tonight": () => {
    const cats = [base("activities"), base("night-out")];
    return { ...cats[0], id: "tonight", name: "Tonight", question: "What shall we do tonight?", filters: mergeFilters(cats), items: cats.flatMap((c) => c.items) };
  },
};

const intentLinks = (skip: string): LandingLink[] => [
  { to: "/what-should-i-eat", label: "What should I eat?", blurb: "Spin for dinner ideas." },
  { to: "/what-should-we-watch", label: "What should we watch?", blurb: "Films and TV, picked for you." },
  { to: "/what-should-we-do-tonight", label: "What should we do tonight?", blurb: "Activities and nights out." },
  { to: "/couples", label: "Decision maker for couples", blurb: "Date-night picks for two." },
  { to: "/who-does-the-dishes", label: "Who does the dishes?", blurb: "Random chore picker." },
].filter((l) => l.to !== `/${skip}`);

export const LANDING_PAGES: LandingPage[] = [
  {
    slug: "couples",
    title: "Decision Maker for Couples – Random Date Night Ideas | DITHER",
    description: "Can't agree on what to do? DITHER is the fun decision maker for couples: spin for date night ideas, pick dinner, choose a film and settle it fairly.",
    h1: "Decision Maker for Couples",
    subtitle: "Date night, dinner, films and “you choose” — settled in seconds.",
    spinLabel: "Spin for date night",
    intro: [
      "“What do you fancy?” “I don't mind, you pick.” Sound familiar? DITHER ends the loop.",
      "Spin the Date Night wheel for 20 ideas — from cosy nights in to evenings out — or narrow it down to stay in, go out, dinner or free.",
      "Still can't agree? Play This or That, or start a two-person vote and let DITHER break the tie.",
    ],
    links: [
      { to: "/category/food", label: "Dinner for two", blurb: "What shall we eat?" },
      { to: "/category/tv-film", label: "Film night", blurb: "What shall we watch?" },
      { to: "/category/night-out", label: "Night out", blurb: "Where are we going?" },
      { to: "/presents", label: "Gift for them", blurb: "What should I buy?" },
      { to: "/tools/this-or-that", label: "This or That for two", blurb: "Two options. One winner." },
      { to: "/group/new", label: "Settle it together", blurb: "Both vote — DITHER breaks ties." },
    ],
    faqs: [
      { q: "What is the best decision maker for couples?", a: "DITHER is a free, fun decision maker for couples. Spin for date night ideas, choose dinner or a film, or vote together when you can't agree." },
      { q: "How do we pick a date night idea?", a: "Tap Spin for date night. DITHER picks from 20 date ideas — filter by stay in, go out, dinner, cosy or free if you want to narrow it down." },
      { q: "We always say “you pick” — can DITHER choose for us?", a: "Yes. That's exactly what it's for. Let the wheel decide and get on with your evening." },
      { q: "Is it fair?", a: "Every option has an equal chance unless you add weights to your own wheel, so nobody can claim the other one always gets their way." },
      { q: "Is DITHER free for couples?", a: "Yes. DITHER is free to use with no sign-up needed." },
    ],
  },
  {
    slug: "what-should-i-eat",
    title: "What Should I Eat? Random Food Picker – Let DITHER Decide",
    description: "Can't decide what to eat? Spin DITHER's random food picker for dinner ideas — pizza, sushi, stir-fry, curry and more. Filter by quick, cheap or healthy.",
    h1: "What Should I Eat?",
    subtitle: "Spin the food wheel and stop the “what do you fancy?” debate.",
    spinLabel: "Spin the food wheel",
    intro: [
      "Staring into the fridge? Scrolling delivery apps for 40 minutes? Let DITHER pick dinner for you.",
      "Spin for 20 food ideas — from pizza night and sushi to a homemade stir-fry — and filter by quick, cheap, healthy, treat yourself or cook at home.",
      "Not feeling it? Spin again, and DITHER will pick that one less often next time.",
    ],
    links: [{ to: "/category/food", label: "Add your own foods", blurb: "Your favourites, on the wheel." }, ...intentLinks("what-should-i-eat")],
    faqs: [
      { q: "What should I eat tonight?", a: "Spin the food wheel above. DITHER picks from 20 dinner ideas and you can filter by quick, cheap, healthy or cook at home." },
      { q: "What should I eat when I can't decide?", a: "Let chance decide. A random food picker removes the decision fatigue — just spin and go." },
      { q: "Can I add my own food options?", a: "Yes. Open the Food category to add your own options, or make a custom wheel with weights for your favourites." },
      { q: "What should we eat as a couple or group?", a: "Spin together, or start a group vote and let everyone pick by name — DITHER breaks any tie." },
    ],
  },
  {
    slug: "what-should-we-watch",
    title: "What Should We Watch? Random Film & TV Picker | DITHER",
    description: "Can't decide what to watch? Spin DITHER's random film and TV picker — comedies, thrillers, sci-fi and feel-good picks. Stop scrolling and start watching.",
    h1: "What Should We Watch?",
    subtitle: "Stop scrolling. Spin and press play.",
    spinLabel: "Spin the film wheel",
    intro: [
      "The average film night starts with half an hour of scrolling. DITHER cuts that to one spin.",
      "Pick from 20 films and series — The Grand Budapest Hotel, Severance, Knives Out, Ted Lasso and more — and filter by comedy, thriller, feel-good, sci-fi or short episodes.",
      "Got your own watchlist? Add it to the TV category and let DITHER choose from that instead.",
    ],
    links: [{ to: "/category/tv-film", label: "Spin your watchlist", blurb: "Add your own shows." }, ...intentLinks("what-should-we-watch")],
    faqs: [
      { q: "What should we watch tonight?", a: "Spin the film wheel above. DITHER picks from 20 films and shows, filtered by mood if you like." },
      { q: "How do I choose a film when we can't agree?", a: "Let DITHER pick at random, or start a group vote where everyone picks by name and DITHER breaks the tie." },
      { q: "Can I use my own watchlist?", a: "Yes. Add your own titles in the TV category and tick “Only pick from my options”." },
      { q: "Is there a random TV show picker?", a: "Yes. Filter by Short episodes for series, or leave it open for films and shows together." },
    ],
  },
  {
    slug: "what-should-we-do-tonight",
    title: "What Should We Do Tonight? Random Night Out Ideas & Activity Picker",
    description: "No plans? No problem. DITHER picks your perfect night out — food, films, activities, and more. The random activity picker for friends and couples.",
    h1: "What Should We Do Tonight?",
    subtitle: "Random night out ideas, activities, and plans — chosen for you.",
    subtitleH2: true,
    spinLabel: "Spin for tonight",
    intro: [
      "Asking “what should we do tonight?” again? DITHER is the random night out generator and activity picker that settles it in one tap — packed with fun things to do tonight, whether you're staying in or heading out.",
      "Free evening, zero plan? DITHER mixes 40 activity and night-out ideas into one wheel so you can stop debating and start doing.",
      "From a board game marathon or living-room karaoke to a comedy club, rooftop drinks or a late-night cinema trip.",
      "Filter by indoors, outdoors, free, cheap, chill or loud to match the mood.",
    ],
    links: [
      { to: "/category/activities", label: "Activities", blurb: "What shall we do?" },
      { to: "/category/night-out", label: "Night out", blurb: "Where are we going?" },
      ...intentLinks("what-should-we-do-tonight"),
    ],
    faqs: [
      { q: "What should we do tonight?", a: "Spin the wheel above. DITHER picks from 40 activity and night-out ideas, with filters for indoors, outdoors, free and more." },
      { q: "What can we do tonight that's free?", a: "Tap the Free filter for ideas like a sunset hunt, a picnic, living-room karaoke or a home workout." },
      { q: "What should I do when I'm bored?", a: "Hit “I'm bored” on DITHER for a surprise pick from every category, or spin this wheel for tonight's plan." },
      { q: "Can friends vote on what to do tonight?", a: "Yes. Start a group vote, share the link and everyone picks by name — DITHER breaks ties." },
    ],
  },
  {
    slug: "random-food-picker",
    title: "Random Food Picker — Can't Decide What to Eat? Let DITHER Choose",
    description: "Stuck on what to eat? DITHER's random food picker chooses for you. Perfect for couples, families, and friends who can never agree on food.",
    h1: "Can't Decide What to Eat?",
    subtitle: "Let DITHER pick your next meal in seconds.",
    subtitleH2: true,
    spinLabel: "Spin the food wheel",
    intro: [
      "“What should I eat?” is the question nobody can answer. DITHER's random food picker answers it for you — a fun food decision maker and choose-what-to-eat app that ends the debate in one spin.",
      "Spin for 20 food ideas, from pizza night and sushi to a homemade stir-fry, and filter by quick, cheap, healthy, treat yourself or cook at home.",
      "Perfect for couples, families and friends who can never agree on food. Want a fair vote instead? Start a group and DITHER breaks the tie.",
    ],
    links: [
      { to: "/category/food", label: "Food category", blurb: "Add your own favourite foods." },
      { to: "/group/new", label: "Vote on dinner", blurb: "Everyone picks — DITHER breaks ties." },
      { to: "/wheels", label: "Make a food wheel", blurb: "Your own options, with weights." },
      { to: "/what-should-we-do-tonight", label: "What should we do tonight?", blurb: "Activities and nights out." },
    ],
    faqs: [
      { q: "How does the random food picker work?", a: "Tap Spin the food wheel. DITHER picks one of 20 food ideas at random — filter first if you only want quick, cheap or healthy options." },
      { q: "What should I eat when I can't decide?", a: "Let DITHER choose. Spin once, go with it, and skip the 40 minutes of scrolling delivery apps." },
      { q: "Can I add my own foods?", a: "Yes. Open the Food category to add your own options, or build a custom wheel with your favourites." },
      { q: "Is it good for couples and families?", a: "Yes. Spin together, or start a group vote so everyone gets a say and DITHER settles any tie." },
    ],
  },
  {
    slug: "couple-decision-maker",
    title: "Couple Decision Maker App — Stop Arguing, Start Deciding",
    description: "The fun decision maker app for couples. Randomly choose food, films, nights out, dates and more. No more arguments — just tap and go.",
    h1: "The Decision Maker App for Couples",
    subtitle: "Random choices for food, dates, films, and nights out.",
    subtitleH2: true,
    spinLabel: "Spin for date night",
    intro: [
      "DITHER is the couple decision maker for every “you pick… no, you pick” moment. It's the app for couples who can't decide, a random date night decision maker and a fair decision maker for couples, all in one.",
      "Spin the Date Night wheel for 20 ideas — cosy nights in, dinners out, films and adventures — or narrow it down to stay in, go out, dinner, cosy or free.",
      "Still can't agree? Play This or That, or start a two-person vote and let DITHER break the tie. No more arguments — just tap and go.",
    ],
    links: [
      { to: "/category/food", label: "Dinner for two", blurb: "What shall we eat?" },
      { to: "/category/tv-film", label: "Film night", blurb: "What shall we watch?" },
      { to: "/category/night-out", label: "Night out", blurb: "Where are we going?" },
      { to: "/couples", label: "Date night ideas", blurb: "More picks for two." },
      { to: "/tools/this-or-that", label: "This or That for two", blurb: "Two options. One winner." },
      { to: "/group/new", label: "Settle it together", blurb: "Both vote — DITHER breaks ties." },
    ],
    faqs: [
      { q: "What is a couple decision maker?", a: "An app that picks for you both — dinner, a film, a date or a night out — so nobody has to be the one who decides." },
      { q: "We can never decide on date night. Can DITHER help?", a: "Yes. Tap Spin for date night and DITHER picks from 20 date ideas, filtered by mood or budget if you like." },
      { q: "Is it fair to both of us?", a: "Every option has an equal chance, so nobody can say the other one always gets their way." },
      { q: "Is it free?", a: "Yes. DITHER is free to use, with no sign-up or download needed." },
    ],
  },
  {
    slug: "who-does-the-dishes",
    mode: "names",
    ideasLabel: "chores DITHER can assign",
    title: "Who Does the Dishes? Random Chore Picker for Housemates | DITHER",
    description: "Settle who does the dishes in seconds. Add your housemates, spin the wheel and let DITHER pick — or assign a random chore too. Free random chore picker.",
    h1: "Who Does the Dishes?",
    subtitle: "Add the household, spin the wheel, no arguments.",
    spinLabel: "Spin the wheel",
    intro: [
      "“I did them last time!” Every house has the argument. DITHER settles it with one spin.",
      "Add your housemates, partner or kids, spin the wheel and DITHER picks who's on dishes duty. Everyone has an equal chance.",
      "Flip on “Pick the chore too” and DITHER also assigns a random job — from taking the bins out to the dreaded bathroom.",
    ],
    links: [
      { to: "/category/chores", label: "Chore wheel", blurb: "Spin for a random chore." },
      { to: "/group/new", label: "Vote on it", blurb: "Everyone votes by name." },
      { to: "/wheels", label: "Make a rota wheel", blurb: "Save your household." },
      { to: "/what-should-i-eat", label: "What should I eat?", blurb: "Spin for dinner ideas." },
      { to: "/what-should-we-do-tonight", label: "What should we do tonight?", blurb: "Activities and nights out." },
      { to: "/couples", label: "Decision maker for couples", blurb: "Date-night picks for two." },
    ],
    faqs: [
      { q: "What's a fair way to decide who does the dishes?", a: "Spin a random wheel with everyone's name on it. Every person has an equal chance, so nobody can say it's rigged." },
      { q: "How does the random chore picker work?", a: "Add your housemates, tap Spin the wheel and DITHER picks one at random. Turn on “Pick the chore too” to assign a random chore as well." },
      { q: "Will DITHER remember our names?", a: "Yes. Names are saved on your device, so the household is ready next time you open the page." },
      { q: "Can we use it for other chores?", a: "Yes. Switch on “Pick the chore too” to assign anything from laundry and hoovering to cleaning the bathroom." },
      { q: "Is it free?", a: "Yes. DITHER is free with no sign-up needed." },
    ],
  },
];

export const landingBySlug = (slug: string) => LANDING_PAGES.find((p) => p.slug === slug);
